import React, { useEffect, useState } from 'react';
import { buildApiUrl } from '../config';
import './Vat.css';

const money = (n) => {
  const v = parseFloat(n);
  if (!Number.isFinite(v)) return '0.00';
  return v.toFixed(2);
};

const Vat = () => {
  const [view, setView] = useState('invoices'); // invoices | expenses
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseTotals, setExpenseTotals] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(buildApiUrl('api/vat/invoices'));
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load VAT invoices');
      setInvoices(data.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load VAT invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async () => {
    if (!fromDate || !toDate) {
      alert('Please select From and To dates');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        buildApiUrl(`api/vat/expenses?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`)
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load VAT expenses');
      setExpenses(data.data || []);
      setExpenseTotals(data.totals || null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load VAT expenses');
      setExpenses([]);
      setExpenseTotals(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'invoices') {
      loadInvoices();
    }
  }, [view]);

  const statusClass = (row) => {
    if (row.vatStatus === 'Paid') {
      return row.remaining < 0 ? 'vat-status-paid negative' : 'vat-status-paid';
    }
    if (row.vatStatus === 'Partial') return 'vat-status-partial';
    return 'vat-status-unpaid';
  };

  const statusLabel = (row) => {
    if (row.vatStatus === 'Paid' && row.remaining < 0) {
      return `Paid (${money(row.remaining)})`;
    }
    return row.vatStatus;
  };

  return (
    <div className="vat-container">
      <h1>VAT Module</h1>

      <div className="vat-radio-group">
        <label className={`vat-radio-label ${view === 'invoices' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="vatView"
            checked={view === 'invoices'}
            onChange={() => setView('invoices')}
          />
          VAT Collected (Invoices)
        </label>
        <label className={`vat-radio-label ${view === 'expenses' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="vatView"
            checked={view === 'expenses'}
            onChange={() => setView('expenses')}
          />
          VAT from Expenses
        </label>
      </div>

      {view === 'expenses' && (
        <div className="vat-filters">
          <div className="form-group">
            <label htmlFor="vat-from">From</label>
            <input
              id="vat-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="vat-to">To</label>
            <input
              id="vat-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button type="button" className="vat-load-btn" onClick={loadExpenses}>
            Load Expenses
          </button>
        </div>
      )}

      {loading && <div className="vat-loading">Loading...</div>}
      {error && <div className="vat-empty">{error}</div>}

      {!loading && !error && view === 'invoices' && (
        <>
          <div className="vat-table-wrap">
            <table className="vat-table">
              <thead>
                <tr>
                  <th>SR#</th>
                  <th>Date</th>
                  <th>Invoice #</th>
                  <th>Company</th>
                  <th>Period</th>
                  <th className="num">Excl VAT</th>
                  <th className="num">VAT</th>
                  <th className="num">Incl VAT</th>
                  <th className="num">Expense VAT</th>
                  <th className="num">Paid</th>
                  <th className="num">Remaining</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="vat-empty">
                      No saved invoices found.
                    </td>
                  </tr>
                ) : (
                  invoices.map((row, idx) => (
                    <tr key={row.sr_no}>
                      <td>{idx + 1}</td>
                      <td>{row.invoice_date ? String(row.invoice_date).slice(0, 10) : '-'}</td>
                      <td>{row.invoice_number}</td>
                      <td>{row.company_name}</td>
                      <td>
                        {row.periodFrom && row.periodTo
                          ? `${row.periodFrom} → ${row.periodTo}`
                          : '-'}
                      </td>
                      <td className="num">{money(row.amountExclVat)}</td>
                      <td className="num">{money(row.vatAmount)}</td>
                      <td className="num">{money(row.amountInclVat)}</td>
                      <td className="num">{money(row.expenseVatTotal)}</td>
                      <td className="num">{money(row.paidTotal)}</td>
                      <td className="num">{money(row.remaining)}</td>
                      <td className={statusClass(row)}>{statusLabel(row)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !error && view === 'expenses' && (
        <>
          <div className="vat-table-wrap">
            <table className="vat-table">
              <thead>
                <tr>
                  <th>SR#</th>
                  <th>Date</th>
                  <th>Expenses Details</th>
                  <th className="num">Amount Excl VAT</th>
                  <th className="num">VAT</th>
                  <th className="num">Amount Incl VAT</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="vat-empty">
                      {fromDate && toDate
                        ? 'No expenses in this range.'
                        : 'Select a date range and click Load Expenses.'}
                    </td>
                  </tr>
                ) : (
                  expenses.map((row, idx) => (
                    <tr key={row.ExpenseID}>
                      <td>{idx + 1}</td>
                      <td>{row.displayDate || row.expenseDate}</td>
                      <td>
                        {[row.expenseType, row.supplierName, row.description]
                          .filter((x) => x && String(x).trim())
                          .join(' | ') || '-'}
                      </td>
                      <td className="num">{money(row.amountExclVat)}</td>
                      <td className="num">{money(row.vatAmount)}</td>
                      <td className="num">{money(row.amountInclVat)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {expenseTotals && expenses.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total</td>
                    <td className="num">{money(expenseTotals.amountExclVat)}</td>
                    <td className="num">{money(expenseTotals.vatAmount)}</td>
                    <td className="num">{money(expenseTotals.amountInclVat)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Vat;
